import {
  type BackgroundMapCustomColorOptions,
  backgroundMapCustomStyleDefaults,
  isCustomBackgroundMapStyle,
} from "@/constants/backgroundMapCustomStyle";
import { Language, languageOptions, onlyBorderStyleLayer } from "@/constants/maptilerStyleOptions";
import type { ParamOptionInputData } from "@/overlay/components/SmartForm/HookInputs/ParamOptionSelectList";
import { SelectInput } from "@/overlay/components/SmartForm/HookInputs/SelectInput";
import { ModeSwitchableForm } from "@/overlay/components/SmartForm/SmartFormUtils/FormComponents/ModeSwitchableFormProps";
import { useFormWithUtils } from "@/overlay/components/SmartForm/SmartFormUtils/FormHooks/useFormUtils";
import type { BaseFormProps } from "@/overlay/components/SmartForm/SmartFormUtils/commonTypeUtils";
import { t } from "@lingui/core/macro";
import type { BackgroundMapLayer, BackgroundMapLayerCreate } from "@mm/metx-workbench.meteomatics.com";
import { CartographicMaterialColorPicker } from "./CartographicMaterialColorPicker";
import { useLanguageOptions, useMaptilerStyleOptions } from "./hooks";

type BackgroundMapLayerPatch = Partial<BackgroundMapLayerCreate | BackgroundMapLayer>;

/**
 * Form data passed from the child components.
 */
export type CartographicMaterialFormData = {
  // Snake case as they get passed as layer props directly.
  layerData: {
    style: string;
    custom_options: {
      map_label_language?: string;
      custom_background_color?: string;
      custom_water_color?: string;
      custom_border_color?: string;
    };
  };
  // Form internal data to construct the above data.
  internal: {
    paramOptionPatch: ParamOptionInputData;
  };
};

type CartographicMaterialLayerFormProps = {
  defaultFormData: BackgroundMapLayerPatch;
} & BaseFormProps<CartographicMaterialFormData>;

/**
 * Responsible for rendering the whole layer form and send back the data on submit hadler.
 */
export function CartographicMaterialCreateForm({
  submitMode,
  onSubmit,
  onCancel,
  defaultFormData,
}: CartographicMaterialLayerFormProps) {
  const { formMethods, formValName } = useFormWithUtils<CartographicMaterialFormData>({ mode: submitMode });
  const { layerData } = formMethods.watch();
  const customColorDefaults = defaultFormData.custom_options as BackgroundMapCustomColorOptions | undefined;
  const selectedStyle = layerData?.style ?? defaultFormData.style ?? "";
  const isCustomStyleSelected = isCustomBackgroundMapStyle(selectedStyle);

  const onSubmitWithFormatting: typeof onSubmit = (formData) => {
    onSubmit(formData);
  };

  const languageOptions = useLanguageOptions();
  const styleOptions = useMaptilerStyleOptions();

  return (
    <ModeSwitchableForm
      onSubmit={formMethods.handleSubmit(onSubmitWithFormatting)}
      onCancel={onCancel}
      submitMode={submitMode}
    >
      <SelectInput
        label={t`Basemap`}
        values={styleOptions}
        instruction={{
          currValue: defaultFormData.style,
          formValueName: formValName("layerData.style"),
          formMethods,
        }}
      />

      {/* For some reason values get's populated only when select is open first time */}
      {!onlyBorderStyleLayer.includes(selectedStyle) && (
        <SelectInput
          label={t`Map Label Language`}
          values={languageOptions}
          instruction={{
            currValue: Language.EN,
            formValueName: formValName("layerData.custom_options.map_label_language"),
            formMethods,
          }}
        />
      )}
      {isCustomStyleSelected && (
        <>
          <CartographicMaterialColorPicker
            label={t`Background Color`}
            instruction={{
              currValue:
                layerData?.custom_options?.custom_background_color ??
                customColorDefaults?.custom_background_color ??
                backgroundMapCustomStyleDefaults.backgroundColor,
              formValueName: formValName("layerData.custom_options.custom_background_color"),
              formMethods,
            }}
          />
          <CartographicMaterialColorPicker
            label={t`Water Color`}
            instruction={{
              currValue:
                layerData?.custom_options?.custom_water_color ??
                customColorDefaults?.custom_water_color ??
                backgroundMapCustomStyleDefaults.waterColor,
              formValueName: formValName("layerData.custom_options.custom_water_color"),
              formMethods,
            }}
          />
          <CartographicMaterialColorPicker
            label={t`Border Color`}
            instruction={{
              currValue:
                layerData?.custom_options?.custom_border_color ??
                customColorDefaults?.custom_border_color ??
                backgroundMapCustomStyleDefaults.borderColor,
              formValueName: formValName("layerData.custom_options.custom_border_color"),
              formMethods,
            }}
          />
        </>
      )}
    </ModeSwitchableForm>
  );
}
